import {VehicleProject, VehicleProjectMaster} from 'types';
import {TFunction} from 'react-i18next';
import {
	renderHeaderXL,
	createZIPXL,
	addXLTable,
	addHTMLImagesAsLinks,
	renderRichtText,
	renderArrayXL,
	renderDateXL,
	renderHTMLStringXL,
	renderStringArrayXL,
} from './ExcelExportCommon';
import Excel from 'exceljs';
import i18n from 'i18n/i18n';
import {
	AssignedRequirementWithID,
	resolveVehicleProjects,
} from 'features/VehicleProjects/AssignedEntities/AssignedRequirementColumns';
import {
	getBodyWorkVariants,
	getDriveVariants,
	getEarliestPosEtVsi,
	getProjectName,
	getTechnologyVariants,
} from 'features/VehicleProjects/components/vehicleProjectColumns';
import {
	renderRegulationNumberXL,
	renderRegulationVersionXL,
} from './XLRequirementsPage';

interface IAssignedRequirementList {
	(
		baselineName: string | undefined,
		entityListItems: AssignedRequirementWithID[],
		columnNames: string[],
		dictVehicleProjects: Record<string, VehicleProject>,
		vehicleProjectMaster: VehicleProjectMaster | undefined,
		t: TFunction,
	): Promise<void>;
}

export const handleClickAssignedRequirements: IAssignedRequirementList = async (
	baselineName,
	entityListItems,
	columnNames,
	dictVehicleProjects,
	vehicleProjectMaster,
	t,
) => {
	const filtRequirement = entityListItems.map(x =>
		resolveVehicleProjects(x, dictVehicleProjects),
	);

	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];
	const arrTables: string[] = [];

	const getHeadingVehProject = (rawHeading: string) => {
		return t(rawHeading, {
			ns: 'features/vehicleprojects',
		});
	};

	const tOptions = {
		ns: 'features/vehicleprojects',
		keyPrefix: 'AssignedRequirementsFields',
	};

	const tOptionsRelevanz = {
		ns: 'features/vehicleprojects',
		keyPrefix: 'ProjectRelevantFields',
	};

	const workbook = new Excel.Workbook();
	const sheet = workbook.addWorksheet(
		'TRACE_' + getHeadingVehProject('AssignedRequirements'),
	);
	const columns = columnNames.map(x => {
		return {name: x, filterButton: true};
	});
	const arrColWidth: number[] = [
		40,
		...new Array(4).fill(25),
		40,
		...new Array(9).fill(25),
		80,
		...new Array(12).fill(25),
	];
	const strPageName = getHeadingVehProject('VehicleProjects');

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		getHeadingVehProject('ModelSeries'),
		vehicleProjectMaster?.modelSeries ?? '',
	);

	row.getCell('C').value = renderRichtText(
		getHeadingVehProject('Generation'),
		vehicleProjectMaster?.generation ?? '',
	);

	if (baselineName) {
		row.getCell('D').value = renderRichtText('Baseline', baselineName);
	}

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	const derivates = renderStringArrayXL(
		(vehicleProjectMaster?.derivates ?? []).filter(x => x.trim() !== ''),
	);
	const sOP = renderDateXL(vehicleProjectMaster?.sopVsi);
	const eOP = renderDateXL(vehicleProjectMaster?.eopVsi);
	const lastEvaluated = renderDateXL(vehicleProjectMaster?.lastEvaluated);

	row.getCell('B').value = renderRichtText(
		getHeadingVehProject('Derivative'),
		derivates,
	);
	row.getCell('C').value = renderRichtText(
		getHeadingVehProject('EarliestSOP'),
		sOP,
	);
	row.getCell('D').value = renderRichtText(
		getHeadingVehProject('LatestEOP'),
		eOP,
	);
	row.getCell('E').value = renderRichtText(
		getHeadingVehProject('LastEvaluated'),
		lastEvaluated,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	const vehicleProjects: VehicleProject[] = Object.values(dictVehicleProjects);
	const bwvr = renderStringArrayXL(getBodyWorkVariants(vehicleProjects));
	const dvr = renderStringArrayXL(getDriveVariants(vehicleProjects));
	const pos = getEarliestPosEtVsi(vehicleProjects) ?? '-';
	const technology = renderStringArrayXL(
		getTechnologyVariants(vehicleProjects),
	);

	row.getCell('B').value = renderRichtText(
		getHeadingVehProject('BodyworkVariants'),
		bwvr,
	);
	row.getCell('C').value = renderRichtText(
		getHeadingVehProject('DriveVariants'),
		dvr,
	);
	row.getCell('D').value = renderRichtText(t('EarliestPOS'), pos);
	row.getCell('E').value = renderRichtText(
		getHeadingVehProject('Technology'),
		technology,
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		getHeadingVehProject('VehicleProjects'),
		vehicleProjects.map(x => getProjectName(x, i18n)).join(';\n'),
	);

	intRowCount++;
	intRowCount++;
	row = sheet.getRow(intRowCount);

	row.font = {
		bold: true,
		name: 'Porsche Next TT',
		size: 10,
		color: {argb: 'FFFFFFFF'},
	};

	const tableName = 'tableRequirements';

	addXLTable(tableName, intRowCount, sheet, columns);

	const table = sheet.getTable(tableName);

	intRowCount++;
	const tempDivElement = document.createElement('div');

	if (filtRequirement.length === 0) {
		table.addRow(new Array(columnNames.length).fill(''));
		intRowCount++;
	}

	for (let ii = 0; ii < filtRequirement.length; ii++) {
		const iAssignedReq = filtRequirement[ii];

		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};
		row.getCell('H').style.font = {
			underline: true,
			bold: false,
			color: {argb: '00B0F0'},
		};
		table.addRow([
			iAssignedReq.vehicleProjects
				.map(vp => getProjectName(vp, i18n))
				.join(';'),
			iAssignedReq.isProjectRelevant
				? t('Yes', tOptionsRelevanz)
				: t('No', tOptionsRelevanz),
			iAssignedReq.projectRelevantComments ?? '',
			iAssignedReq.addedAutomatically
				? t('true', tOptions)
				: t('false', tOptions),
			iAssignedReq.addReason === 'AutomaticAssignment'
				? t('AutomaticAssignment', tOptions)
				: iAssignedReq.addReason,
			iAssignedReq.name,
			{
				text: iAssignedReq.requirementId,
				hyperlink: window.location.origin + '/requirements/' + iAssignedReq.id,
			},
			iAssignedReq.version ?? 'V1.0',
			renderStringArrayXL(renderRegulationNumberXL(iAssignedReq.regulations)),
			renderStringArrayXL(
				renderRegulationVersionXL(iAssignedReq.regulatoryDocuments),
			),
			renderArrayXL(iAssignedReq.markets),
			renderArrayXL(iAssignedReq.vexClusters),
			renderDateXL(iAssignedReq.dateEffective),
			renderDateXL(iAssignedReq.dateEffective),
			t(iAssignedReq.status ?? '', {
				ns: 'common/enums',
				keyPrefix: 'RequirementStatus',
			}),
			renderHTMLStringXL('', iAssignedReq.definition),
			renderArrayXL(iAssignedReq.systemLevels),

			renderArrayXL(iAssignedReq.keywords),
			renderArrayXL(iAssignedReq.tags),
			iAssignedReq.createdBy.name,
			renderDateXL(iAssignedReq.modifiedAt),
			t(iAssignedReq.category, {
				ns: 'common/enums',
				keyPrefix: 'RequirementCategory',
			}),
		]);

		intRowCount++;
		tempDivElement.innerHTML = iAssignedReq.definition
			? '<div>' + iAssignedReq.definition + '</div>'
			: '<div></div>';

		intRowCount = addHTMLImagesAsLinks(
			intRowCount,
			iAssignedReq.requirementId as string,
			arrImagesName,
			arrImagesUri,
			'Q',
			15,
			tempDivElement,
			sheet,
			table,
			t,
		);
	}

	table.commit();

	const strZipName = t('RequirementsHeader', {ns: 'features/requirements'});
	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		[],
	);
};
